import { z } from "zod";
import {
  type InsightsQuery,
  INSIGHTS_ACTIVITY_GRANULARITIES,
  INSIGHTS_ACTIVITY_SUMMARY_KEYS,
  INSIGHTS_BREAKDOWN_KEYS,
  INSIGHTS_PAYOUT_TYPES,
  INSIGHTS_RANGE_PRESETS,
  INSIGHTS_SUMMARY_METRIC_KEYS,
} from "@artha/types";

export const InsightsRangePresetSchema = z.enum(INSIGHTS_RANGE_PRESETS);

export const InsightsQuerySchema: z.ZodType<InsightsQuery> = z
  .object({
    rangePreset: InsightsRangePresetSchema.optional(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    payoutType: z.enum(INSIGHTS_PAYOUT_TYPES).optional(),
  })
  .refine(
    (value) =>
      Boolean(value.rangePreset) ||
      (Boolean(value.startDate) && Boolean(value.endDate)),
    {
      message: "Either rangePreset or both startDate and endDate are required",
      path: ["rangePreset"],
    },
  )
  .refine(
    (value) =>
      !value.startDate ||
      !value.endDate ||
      new Date(value.startDate).getTime() <= new Date(value.endDate).getTime(),
    {
      message: "startDate must be before or equal to endDate",
      path: ["startDate"],
    },
  );

export const InsightsSummaryMetricSchema = z.object({
  key: z.enum(INSIGHTS_SUMMARY_METRIC_KEYS),
  value: z.number(),
  change: z.number(),
});

export const InsightsBreakdownMetricSchema = z.object({
  key: z.enum(INSIGHTS_BREAKDOWN_KEYS),
  value: z.number(),
});

export const InsightsActivitySummaryMetricSchema = z.object({
  key: z.enum(INSIGHTS_ACTIVITY_SUMMARY_KEYS),
  value: z.number(),
  change: z.number(),
});

export const InsightsAudienceSchema = z.object({
  totalAudience: z.number(),
  audienceGrowth: z.number(),
});

export const InsightsAverageRpaSchema = z.object({
  value: z.number(),
  change: z.number(),
});

export const InsightsTopJobSchema = z.object({
  jobId: z.string(),
  refId: z.string().nullable(),
  title: z.string(),
  company: z.string(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  postedDate: z.string().nullable().optional(),
  validClicks: z.number(),
  approvedApplications: z.number(),
  botClicks: z.number(),
  foreignClicks: z.number(),
  duplicateClicks: z.number(),
  earnings: z.number(),
  rpa: z.number(),
  rpc: z.number(),
  rph: z.number(),
  payoutType: z.enum(INSIGHTS_PAYOUT_TYPES).nullable(),
  payoutValue: z.number().nullable(),
});

export const InsightsOverviewResponseSchema = z.object({
  rangeLabel: z.string(),
  comparisonLabel: z.string(),
  summaryMetrics: z.array(InsightsSummaryMetricSchema),
  breakdown: z.array(InsightsBreakdownMetricSchema),
  averageRpa: InsightsAverageRpaSchema,
  activitySummary: z.array(InsightsActivitySummaryMetricSchema),
  audience: InsightsAudienceSchema,
  topJobs: z.array(InsightsTopJobSchema),
  isEmpty: z.boolean(),
});

export const InsightsActivityBucketSchema = z.object({
  label: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  validClicks: z.number(),
  approvedApplications: z.number(),
  botClicks: z.number(),
  foreignClicks: z.number(),
  duplicateClicks: z.number(),
  earnings: z.number(),
  rpa: z.number(),
  rpc: z.number(),
  rph: z.number(),
});

export const InsightsActivityResponseSchema = z.object({
  title: z.string(),
  bucketGranularity: z.enum(INSIGHTS_ACTIVITY_GRANULARITIES),
  rows: z.array(InsightsActivityBucketSchema),
});

export const InsightsJobsResponseSchema = z.object({
  rangeLabel: z.string(),
  comparisonLabel: z.string(),
  jobs: z.array(InsightsTopJobSchema),
});

export type InsightsRangePresetDto = z.infer<typeof InsightsRangePresetSchema>;
export type InsightsQueryDto = InsightsQuery;
export type InsightsSummaryMetricDto = z.infer<
  typeof InsightsSummaryMetricSchema
>;
export type InsightsBreakdownMetricDto = z.infer<
  typeof InsightsBreakdownMetricSchema
>;
export type InsightsActivitySummaryMetricDto = z.infer<
  typeof InsightsActivitySummaryMetricSchema
>;
export type InsightsAudienceDto = z.infer<typeof InsightsAudienceSchema>;
export type InsightsAverageRpaDto = z.infer<typeof InsightsAverageRpaSchema>;
export type InsightsTopJobDto = z.infer<typeof InsightsTopJobSchema>;
export type InsightsOverviewResponseDto = z.infer<
  typeof InsightsOverviewResponseSchema
>;
export type InsightsActivityBucketDto = z.infer<
  typeof InsightsActivityBucketSchema
>;
export type InsightsActivityResponseDto = z.infer<
  typeof InsightsActivityResponseSchema
>;
export type InsightsJobsResponseDto = z.infer<
  typeof InsightsJobsResponseSchema
>;

// Proof metadata attached to the recommended-jobs response so the creator
// dashboard can show how many audience clicks seeded these recommendations.
export const RecommendedJobsProofSchema = z.object({
  totalAudienceClicks: z.number(),
  rangeLabel: z.string(),
});

export const RecommendedJobsResponseSchema = z.object({
  items: z.array(z.any()),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean().optional(),
  proof: RecommendedJobsProofSchema.optional(),
});

export type RecommendedJobsProofDto = z.infer<
  typeof RecommendedJobsProofSchema
>;
export type RecommendedJobsResponseDto = z.infer<
  typeof RecommendedJobsResponseSchema
>;
